import SaveIcon from '@mui/icons-material/Save';
import { Button, CircularProgress } from '@mui/material';
import { endOfDay, endOfMonth, endOfYear, startOfDay, startOfMonth, startOfYear } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Page } from '../../shared';
import { ReportFilter } from '../components/report-filter.component';
import { Report } from '../components/report.component';
import { useReportExport } from '../hooks/use-report-export.hook';
import { useReport } from '../hooks/use-report.hook';
import { FilterType, IReportFilter } from '../models/report-filter.model';
import {
    IRunReportDateFilter,
    IRunReportFilter,
    IRunReportListFilter,
    IRunReportNumberFilter,
    IRunReportSearchFilter,
    IRunReportStringFilter,
} from '../models/run-report.model';

export const ReportPage: FC = () => {
    const { t } = useTranslation();
    const { id = '' } = useParams();
    const { data: report, isPending: isReportLoading } = useReport(id);
    const { mutate: exportReport, isPending: exportIsPending } = useReportExport();
    const availableFilters = useMemo(() => report?.filters ?? [], [report]);
    const hasFilters = useMemo(() => availableFilters.length > 0, [availableFilters]);
    const hasRequiredFilters = useMemo(() => availableFilters.some(({ required }) => required), [availableFilters]);
    const defaultFilters = useDefaultFilters(availableFilters);
    const [filters, setFilters] = useState<IRunReportFilter[]>([]);
    const isPageLoading = isReportLoading || (hasRequiredFilters && !filters.length);

    useEffect(() => {
        setFilters(defaultFilters);
    }, [defaultFilters]);

    const onExport = useCallback(() => {
        exportReport({ id, filters: filters, name: report?.name || '' });
    }, [exportReport, id, filters, report]);

    return (
        <Page
            title={report?.name}
            loading={isPageLoading}
            filter={
                hasFilters && (
                    <ReportFilter filters={filters} availableFilters={availableFilters} onChange={setFilters} />
                )
            }
            actions={
                <Button
                    variant="contained"
                    onClick={onExport}
                    disabled={exportIsPending}
                    startIcon={exportIsPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                    {exportIsPending ? t('exportIsPending') : t('export')}
                </Button>
            }
        >
            {report && <Report report={report} filters={filters} />}
        </Page>
    );
};

function useDefaultFilters(filters: IReportFilter[]): IRunReportFilter[] {
    return useMemo(
        () =>
            filters
                .filter(({ required }) => required)
                .map((filter) => {
                    switch (filter.type) {
                        case FilterType.DATE:
                            return getDefaultDateFilter(filter);
                        case FilterType.SELECT:
                            return getDefaultSelectFilter(filter);
                        case FilterType.SINGLE_SELECT:
                            return getDefaultSingleSelectFilter(filter);
                        case FilterType.NUMBER:
                            return getDefaultNumberFilter(filter);
                        case FilterType.SEARCH:
                            return getDefaultSearchFilter(filter);
                    }
                }),
        [filters],
    );
}

function getDefaultNumberFilter(filter: IReportFilter): IRunReportNumberFilter {
    return {
        type: FilterType.NUMBER,
        column: filter.column,
        min: 0,
        max: 100,
    };
}

function getDefaultDateFilter(filter: IReportFilter): IRunReportDateFilter {
    let start, end;
    if (filter.dateFilterPeriod === 'day') {
        start = startOfDay(new Date()).toISOString();
        end = endOfDay(new Date()).toISOString();
    } else if (filter.dateFilterPeriod === 'year') {
        start = startOfYear(new Date()).toISOString();
        end = endOfYear(new Date()).toISOString();
    } else {
        start = startOfMonth(new Date()).toISOString();
        end = endOfMonth(new Date()).toISOString();
    }

    return {
        type: FilterType.DATE,
        column: filter.column,
        start,
        end,
    };
}

function getDefaultSelectFilter(filter: IReportFilter): IRunReportListFilter {
    return {
        type: FilterType.SELECT,
        column: filter.column,
        values: filter.values?.map((v) => v.value) ?? [],
    };
}

function getDefaultSingleSelectFilter(filter: IReportFilter): IRunReportStringFilter {
    return {
        type: FilterType.SINGLE_SELECT,
        column: filter.column,
        value: filter.values?.[0]?.value || '',
    };
}

function getDefaultSearchFilter(filter: IReportFilter): IRunReportSearchFilter {
    return {
        type: FilterType.SEARCH,
        column: filter.column,
        value: '',
    };
}
